import { useEffect, useState } from 'react';
import { cn, initials } from '@/lib/utils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SOMEBODY'S FACE, OR THEIR INITIALS
 *
 * One component for every place a person is shown, because the fallback is the
 * whole point and it is the part that would have been written differently in
 * each of them.
 *
 * Initials are not a placeholder here — they are what most people will have,
 * and they are drawn identically whether the person has no picture, has one
 * that failed to load, or has one whose signed URL has quietly expired. A
 * broken-image icon in a sidebar is worse than initials in every one of those
 * cases, and only the first is a state anybody chose.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function UserAvatar({
  firstName,
  lastName,
  avatarUrl,
  className,
  /** Applied only to the initials, which need a size the image does not. */
  textClassName,
}: {
  firstName: string;
  lastName: string;
  avatarUrl: string | null | undefined;
  className?: string;
  textClassName?: string;
}) {
  const [failed, setFailed] = useState(false);

  /**
   * A new URL gets a fresh chance.
   *
   * Without this, one failure latches for the life of the component — so
   * replacing a picture that had failed to load would show initials no matter
   * how good the new one was, because `failed` was still true from the old.
   */
  useEffect(() => setFailed(false), [avatarUrl]);

  const shared = cn(
    'flex shrink-0 items-center justify-center overflow-hidden bg-brand-800 font-semibold text-white',
    className,
  );

  if (avatarUrl && !failed) {
    return (
      <span className={shared}>
        <img
          src={avatarUrl}
          alt=""
          // `h-full w-full` plus `object-cover`: a portrait photo fills the
          // frame and crops rather than letterboxing inside it.
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }

  return (
    <span className={cn(shared, textClassName)} aria-hidden>
      {initials(firstName, lastName)}
    </span>
  );
}
